import Typography from "@/design-system/Typography";
import Button from "@/design-system/button";
import Container from "@/design-system/container";
import { languageSelector, useViewportInfoContext } from "@/src/contexts";
import { showDidomiConsentModal } from "@/src/didomi-consent";
import LanguageSwitcher from "@/src/shared/language-switcher/language-switcher";
import MobileStores from "@/src/shared/mobile-stores";
import * as stylex from "@stylexjs/stylex";

import { colors } from "../../../styles/tokens.stylex";
import SocialMediaLink from "../social-media-link";
import Resources from "./Footer.Resources";
import ResourcesEn from "./Footer.Resources.En";

export interface IFooter {
    categories: IFooterCategory[];
}

export interface IFooterCategory {
    name: string;
    url: string;
    footerSubCategories: IFooterSubCategory[];
}

export interface IFooterSubCategory {
    name: string;
    url: string;
}

export default function Footer(props: IFooter) {
    const { isMobile } = useViewportInfoContext();
    const resources = languageSelector(Resources, ResourcesEn);

    return (
        <>
            <hr {...stylex.props(styles.hr, styles.hrTop)} />
            <Container Element="footer" styleX={styles.container}>
                <nav {...stylex.props(styles.navContainer, isMobile && styles.navContainerMobile)}>
                    {props.categories?.map(
                        (item) =>
                            item?.url && (
                                <ul key={item.url} {...stylex.props(styles.ulContainer)}>
                                    <li>
                                        <Button
                                            as="a"
                                            variant="none"
                                            size="None"
                                            href={item.url}
                                            isExternal
                                            styleX={[styles.primaryButton, styles.fontSetter]}
                                        >
                                            {item.name}
                                        </Button>
                                    </li>
                                    {item.footerSubCategories?.map(
                                        (subItem) =>
                                            subItem?.url && (
                                                <li key={subItem.url}>
                                                    <Button
                                                        as="a"
                                                        variant="none"
                                                        size="None"
                                                        href={subItem.url}
                                                        isExternal
                                                        styleX={[styles.secondaryButton, styles.fontSetter]}
                                                    >
                                                        {subItem.name}
                                                    </Button>
                                                </li>
                                            ),
                                    )}
                                </ul>
                            ),
                    )}
                </nav>
                <hr {...stylex.props(styles.hr)} />
                <Typography
                    textElement="p"
                    dangerouslySetInnerHTML={{ __html: resources.GoodieDescription }}
                    styleX={styles.fontSetter}
                />
                <hr {...stylex.props(styles.hr)} />
                <div {...stylex.props(styles.socialWrapper, isMobile && styles.socialWrapperMobile)}>
                    <div {...stylex.props(styles.mobileStores, isMobile && styles.mobileStoresMobile)}>
                        <MobileStores referrer="goodie_footer" />
                    </div>

                    <div {...stylex.props(styles.socialLinks, isMobile && styles.socialLinksMobile)}>
                        <SocialMediaLink
                            socialPlatform="instagram"
                            href="https://www.instagram.com/goodie.pl/"
                            trackerIndex="2"
                        />
                        <SocialMediaLink
                            socialPlatform="facebook"
                            href="https://www.facebook.com/GoodiePolska/"
                            trackerIndex="1"
                        />
                        <SocialMediaLink
                            socialPlatform="youtube"
                            href="https://www.youtube.com/channel/UCJZWVdMU6nihDFH62AOLOdg"
                            trackerIndex="4"
                        />
                        <SocialMediaLink
                            socialPlatform="linkedin"
                            href="https://pl.linkedin.com/company/goodie.pl"
                            trackerIndex="3"
                        />
                        <SocialMediaLink
                            socialPlatform="millennium"
                            href="https://www.bankmillennium.pl/klienci-indywidualni"
                            trackerIndex="5"
                        />
                    </div>

                    <LanguageSwitcher styleX={styles.languageSelect} />
                </div>
                <hr {...stylex.props(styles.hr)} />
                <nav {...stylex.props(styles.linksNav, isMobile && styles.linksNavMobile)}>
                    <Button
                        variant="none"
                        size="M"
                        href={"/o-goodie"}
                        as="a"
                        isExternal
                        styleX={styles.links}
                    >
                        {resources.AboutUs}
                    </Button>
                    <Button
                        variant="none"
                        size="M"
                        href={"/regulamin"}
                        as="a"
                        isExternal
                        styleX={styles.links}
                    >
                        {resources.Rules}
                    </Button>
                    <Button
                        variant="none"
                        size="M"
                        href={"/ochrona-danych"}
                        as="a"
                        isExternal
                        styleX={styles.links}
                    >
                        {resources.PrivacyPolicy}
                    </Button>

                    <Button
                        variant="none"
                        size="M"
                        as="button"
                        styleX={styles.links}
                        onClick={showDidomiConsentModal}
                    >
                        {resources.PrivacySettings}
                    </Button>
                    <Button variant="none" size="M" href={"/pomoc"} as="a" styleX={styles.links} isExternal>
                        {resources.Help}
                    </Button>
                    <Button
                        variant="none"
                        size="M"
                        href={"https://lp.goodie.pl/goodie-dla-partnera"}
                        as="a"
                        styleX={styles.links}
                    >
                        {resources.ForPartners}
                    </Button>
                    <Button variant="none" size="M" href={"/kontakt"} as="a" styleX={styles.links} isExternal>
                        {resources.Contact}
                    </Button>
                    <Button
                        variant="none"
                        size="M"
                        href={"/mapa-strony"}
                        as="a"
                        styleX={styles.links}
                        isExternal
                    >
                        {resources.SitemapIndex}
                    </Button>
                    <Button
                        variant="none"
                        size="M"
                        href={"/deklaracja-dostepnosci"}
                        as="a"
                        styleX={styles.links}
                        isExternal
                    >
                        {resources.Accessibility}
                    </Button>
                </nav>
                <Typography textElement="p" styleX={styles.copyrights}>
                    {resources.Copyright}
                </Typography>
            </Container>
        </>
    );
}

const styles = stylex.create({
    container: {
        textAlign: "center",
        marginBottom: "1rem",
    },
    navContainer: {
        gap: "2rem",
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr",
        textAlign: "left",
    },
    navContainerMobile: {
        gridTemplateColumns: "1fr 1fr",
    },
    ulContainer: {
        flex: "0 0 200px",
        gap: "0.625em",
        display: "flex",
        flexDirection: "column",
    },

    primaryButton: {
        ":hover::before": {
            transform: "scaleX(1)",
            visibility: "visible",
        },
        color: colors.b_BrandNavy,
        fontWeight: "bold",
        position: "relative",
        textTransform: "capitalize",
        "::before": {
            backgroundColor: colors.b_BrandNavy,
            content: "",
            position: "absolute",
            transform: "scaleX(0)",
            transitionDuration: ".3s",
            transitionProperty: "transform",
            transitionTimingFunction: "ease-out",
            visibility: "hidden",
            bottom: 0,
            height: "2px",
            left: 0,
            width: "100%",
        },
    },
    secondaryButton: {
        color: { default: colors.fg_Primary, ":hover": colors.fg_Accent },
    },
    socialWrapper: {
        gap: "1rem",
        alignItems: "center",
        display: "flex",
        justifyContent: "flex-end",
    },
    socialWrapperMobile: {
        gap: "1rem",
        flexDirection: "column",
    },
    mobileStores: {
        flexGrow: 1,
        order: 1,
        textAlign: "left",
    },
    mobileStoresMobile: {
        order: 2,
        textAlign: "center",
    },
    socialLinks: {
        display: "flex",
        order: 2,
    },
    socialLinksMobile: {
        order: 1,
    },
    languageSelect: {
        color: colors.fg_Accent,
        fontSize: "1rem",
        lineHeight: 1,
        order: 3,
    },
    fontSetter: {
        fontSize: "0.875rem",
    },
    linksNav: {
        gap: "1rem",
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
    },
    linksNavMobile: {
        display: "grid",
        gridRowGap: 0,
        gridTemplateColumns: "repeat(2, 50%)",
    },
    links: {
        color: colors.fg_Primary,
        fontSize: "0.875rem",
        fontWeight: { default: 400, ":hover": "bold" },
    },
    copyrights: {
        color: colors.fg_Tertiary,
        fontSize: "0.75rem",
        marginTop: "25px",
    },
    hrTop: {
        marginTop: 0,
    },
    hr: {
        marginBlock: "2rem",
        marginInline: 0,
        overflow: "visible",
        boxSizing: "border-box",
        borderTopColor: "#e4e4e4",
        borderTopStyle: "solid",
        borderTopWidth: 1,
        height: 0,
    },
});
